import {
  apiCreateUserPhone,
  apiGetBanks,
  apiGetCards,
  apiGetCardTemplates,
  apiGetCurrentUser,
  apiGetSbpPayment,
  apiGetTransaction,
  apiGetTransactions,
  apiIssueCard,
  apiPrepareCardBlock,
  apiPrepareCardTopup,
  apiPrepareCardWithdraw,
  apiPrepareIssueCard,
  apiRetryUserPhone,
  apiTopupCard,
  apiVerifyUserPhone,
  apiWithdrawCard,
} from '@shared-core/features/backend/api'
import {
  isCardIssuePaymentActive,
  type ModelCardIssueInvoice,
  type ModelCardTemplateId,
  type WithdrawBankId,
} from '@shared-core/features/backend/models'
import { getPaymentSessionVersion } from '@shared-core/features/backend/session'
import {
  cardBlockPreparationSaved,
  cardBlockPreparingChanged,
  cardIssueChanged,
  patchBackendState,
  storeKey,
} from '@shared-core/features/backend/slice'
import { handleErrors } from '@shared-core/features/notifications'
import { API_KNOWN_ERRORS } from '@shared-core/services/api'
import type { AppDispatch } from '@shared-core/store/types'
import { createAsyncThunk } from '@shared-core/store/utils'

const reportFailure = (
  dispatch: AppDispatch,
  response: { errors?: Record<string, unknown>; status?: string | null; meta?: Record<string, unknown> | null } | null,
) => {
  dispatch(handleErrors(response, { defaultError: { error: API_KNOWN_ERRORS.REQUEST_ERROR } }))
}

export const fetchBanks = createAsyncThunk('backend/fetchBanks', async (_, { dispatch }) => {
  const sessionVersion = getPaymentSessionVersion()
  dispatch(patchBackendState({ isBanksLoading: true, banksError: null }))
  const response = await apiGetBanks()

  if (sessionVersion !== getPaymentSessionVersion()) return null

  if (response?.status === 'ok' && response.data) {
    dispatch(patchBackendState({ banks: response.data, isBanksLoading: false, banksError: null }))
    return response.data
  }

  reportFailure(dispatch, response)
  dispatch(patchBackendState({ banks: null, isBanksLoading: false, banksError: API_KNOWN_ERRORS.REQUEST_ERROR }))
  return null
})

export const fetchCards = createAsyncThunk('backend/fetchCards', async (_, { dispatch }) => {
  const sessionVersion = getPaymentSessionVersion()
  dispatch(patchBackendState({ isCardsLoading: true, cardsError: null }))
  const response = await apiGetCards()

  if (sessionVersion !== getPaymentSessionVersion()) return null

  if (response?.status === 'ok' && response.data) {
    dispatch(patchBackendState({ cards: response.data, isCardsLoading: false, cardsError: null }))
    return response.data
  }

  reportFailure(dispatch, response)
  dispatch(patchBackendState({ isCardsLoading: false, cardsError: API_KNOWN_ERRORS.REQUEST_ERROR }))
  return null
})

export const fetchCardTemplates = createAsyncThunk(
  'backend/fetchCardTemplates',
  async (_, { dispatch }) => {
    const sessionVersion = getPaymentSessionVersion()
    dispatch(patchBackendState({ isCardTemplatesLoading: true, cardTemplatesError: null }))
    const response = await apiGetCardTemplates()

    if (sessionVersion !== getPaymentSessionVersion()) return null

    if (response?.status === 'ok' && response.data) {
      dispatch(
        patchBackendState({ cardTemplates: response.data, isCardTemplatesLoading: false, cardTemplatesError: null }),
      )
      return response.data
    }

    reportFailure(dispatch, response)
    dispatch(patchBackendState({ isCardTemplatesLoading: false, cardTemplatesError: API_KNOWN_ERRORS.REQUEST_ERROR }))
    return null
  },
  { condition: (_, { getState }) => !getState()[storeKey].isCardTemplatesLoading },
)

export const fetchTransactions = createAsyncThunk('backend/fetchTransactions', async (_, { dispatch }) => {
  const sessionVersion = getPaymentSessionVersion()
  dispatch(patchBackendState({ isTransactionsLoading: true, transactionsError: null }))
  const response = await apiGetTransactions({})

  if (sessionVersion !== getPaymentSessionVersion()) return null

  if (response?.status === 'ok' && response.data) {
    dispatch(patchBackendState({ transactions: response.data, isTransactionsLoading: false, transactionsError: null }))
    return response.data
  }

  reportFailure(dispatch, response)
  dispatch(patchBackendState({ isTransactionsLoading: false, transactionsError: API_KNOWN_ERRORS.REQUEST_ERROR }))
  return null
})

export const fetchCurrentUser = createAsyncThunk('backend/fetchCurrentUser', async (_, { dispatch }) => {
  const sessionVersion = getPaymentSessionVersion()
  dispatch(patchBackendState({ isUserLoading: true, userError: null }))
  const response = await apiGetCurrentUser()

  if (sessionVersion !== getPaymentSessionVersion()) return null

  if (response?.status === 'ok' && response.data) {
    dispatch(patchBackendState({ user: response.data, isUserLoading: false, userError: null }))
    return response.data
  }

  reportFailure(dispatch, response)
  dispatch(patchBackendState({ isUserLoading: false, userError: API_KNOWN_ERRORS.REQUEST_ERROR }))
  return null
})

export const createUserPhone = createAsyncThunk('backend/createUserPhone', async (phone: string, { dispatch }) => {
  const response = await apiCreateUserPhone({ data: { phone } })
  const idempotency = response?.status === 'ok' ? response.data?.idempotency : null

  if (!response || !idempotency) {
    reportFailure(dispatch, response)
    return null
  }

  return response.data
})

export const retryUserPhone = createAsyncThunk('backend/retryUserPhone', async (idempotency: string, { dispatch }) => {
  const response = await apiRetryUserPhone({ data: { idempotency } })
  const nextIdempotency = response?.status === 'ok' ? response.data?.idempotency : null

  if (!response || !nextIdempotency) {
    reportFailure(dispatch, response)
    return null
  }

  return response.data
})

export const verifyUserPhone = createAsyncThunk(
  'backend/verifyUserPhone',
  async ({ idempotency, otp }: { idempotency: string; otp: string }, { dispatch }) => {
    const response = await apiVerifyUserPhone({ data: { idempotency, otp } })

    if (response?.status !== 'ok' || response.data?.verified !== true) {
      reportFailure(dispatch, response)
      return null
    }

    await dispatch(fetchCurrentUser())
    return true
  },
)

// Подготовка живёт вне шита: ответ сохраняется даже после размонтирования экрана.
export const prepareCardBlock = createAsyncThunk(
  'backend/prepareCardBlock',
  async (cardId: string, { dispatch }) => {
    const sessionVersion = getPaymentSessionVersion()
    dispatch(cardBlockPreparingChanged({ cardId, isPreparing: true }))

    const response = await apiPrepareCardBlock({ routeParams: { id: cardId } })
    const data = response?.data
    const sessionEnded = sessionVersion !== getPaymentSessionVersion()

    if (sessionEnded) return null

    if (response?.status !== 'ok' || !data) {
      reportFailure(dispatch, response)
      dispatch(cardBlockPreparingChanged({ cardId, isPreparing: false }))
      return null
    }

    const preparation = {
      idempotency: data.idempotency,
      channel: data.channel,
      expiresAt: Date.now() + data.validTime * 1000,
    }

    dispatch(cardBlockPreparationSaved({ cardId, preparation }))
    return preparation
  },
  {
    condition: (cardId, { getState }) => {
      const state = getState()[storeKey]
      const preparation = state.cardBlockPreparations[cardId]
      return !state.cardBlockPreparing[cardId] && (!preparation || preparation.expiresAt <= Date.now())
    },
  },
)

export const restoreCardIssue = createAsyncThunk(
  'backend/restoreCardIssue',
  async ({ templateId, txId }: { templateId?: ModelCardTemplateId; txId?: string }, { dispatch, getState }) => {
    // Восстанавливаем счёт после перезапуска или входа из истории без повторного выпуска.
    const sessionVersion = getPaymentSessionVersion()
    const state = getState()[storeKey]
    const saved = Object.values({
      ...(state.pendingCardIssueInvoice ? { [state.pendingCardIssueInvoice.txId]: state.pendingCardIssueInvoice } : {}),
      ...state.pendingCardIssueInvoices,
    }).find(
      (invoice) =>
        (txId ? invoice.txId === txId : invoice.templateId === templateId) && isCardIssuePaymentActive(invoice),
    )

    if (saved && (txId ? saved.txId === txId : saved.templateId === templateId) && isCardIssuePaymentActive(saved)) {
      dispatch(cardIssueChanged({ invoice: saved, isLoading: false, error: null }))
      return saved
    }

    const cards = getState()[storeKey].cards ?? (await dispatch(fetchCards()).unwrap())

    if (sessionVersion !== getPaymentSessionVersion()) return null

    const transactions = txId ? null : await dispatch(fetchTransactions()).unwrap()
    const transaction = txId
      ? (await apiGetTransaction({ routeParams: { id: txId } }))?.data
      : transactions?.find(
          (item) =>
            item.type === 'cardIssue' &&
            (item.templateId ?? cards?.find((card) => card.id === item.cardId)?.templateId) === templateId &&
            (item.status === 'pending' || item.status === 'processing'),
        )

    if (sessionVersion !== getPaymentSessionVersion() || transaction?.type !== 'cardIssue') return null

    const payment = (await apiGetSbpPayment({ routeParams: { id: transaction.txId ?? transaction.txHash } }))?.data
    const resolvedTemplateId =
      transaction.templateId ?? templateId ?? cards?.find((card) => card.id === payment?.cardId)?.templateId

    if (sessionVersion !== getPaymentSessionVersion() || !payment || !resolvedTemplateId) return null

    const invoice: ModelCardIssueInvoice = {
      txId: payment.txId,
      cardId: payment.cardId,
      templateId: resolvedTemplateId,
      amount: payment.amount,
      currency: payment.currency,
      usdAmount: null,
      rate: null,
      qrUrl: payment.qrUrl,
      paymentLink: payment.qrUrl,
      payeePhone: null,
      state: payment.state,
      expiresAt: payment.expiresAt,
    }

    dispatch(cardIssueChanged({ invoice, isLoading: false, error: null }))
    return invoice
  },
)

export const issueCard = createAsyncThunk(
  'backend/issueCard',
  async ({ templateId }: { templateId: ModelCardTemplateId }, { dispatch, getState }) => {
    // Подготовка и выпуск принадлежат одной сессии: после logout продолжать оплату нельзя.
    const sessionVersion = getPaymentSessionVersion()
    dispatch(cardIssueChanged({ invoice: getState()[storeKey].pendingCardIssueInvoice, isLoading: true, error: null }))
    const restored = await dispatch(restoreCardIssue({ templateId })).unwrap()

    if (sessionVersion !== getPaymentSessionVersion()) return null

    if (isCardIssuePaymentActive(restored)) return restored

    dispatch(cardIssueChanged({ invoice: null, isLoading: true, error: null }))

    const fail = (response: Parameters<typeof reportFailure>[1]) => {
      if (sessionVersion !== getPaymentSessionVersion() || !getState()[storeKey].cardIssue.isLoading) return null

      reportFailure(dispatch, response)
      dispatch(
        cardIssueChanged({
          invoice: getState()[storeKey].cardIssue.invoice,
          isLoading: false,
          error: API_KNOWN_ERRORS.REQUEST_ERROR,
        }),
      )
      return null
    }

    const prepared = await apiPrepareIssueCard({ data: { templateId } })
    const idempotency = prepared?.status === 'ok' ? prepared.data?.idempotency : null

    if (sessionVersion !== getPaymentSessionVersion()) return null

    if (!idempotency) {
      if (prepared?.errors?.base?.includes('CARD_ALREADY_ISSUED')) {
        const invoice = await dispatch(restoreCardIssue({ templateId })).unwrap()

        if (isCardIssuePaymentActive(invoice)) return invoice

        if (sessionVersion !== getPaymentSessionVersion()) return null

        dispatch(cardIssueChanged({ invoice, isLoading: true, error: null }))
      }
      return fail(prepared)
    }

    const response = await apiIssueCard({ data: { templateId, idempotency } })

    if (sessionVersion !== getPaymentSessionVersion()) return null

    if (response?.status !== 'ok' || !response.data) return fail(response)

    const invoice = { ...response.data, createdAt: Date.now() }

    if (!getState()[storeKey].cardIssue.isLoading) return invoice

    dispatch(cardIssueChanged({ invoice, isLoading: false, error: null }))
    return invoice
  },
  {
    condition: ({ templateId }, { getState }) => {
      const issue = getState()[storeKey].cardIssue
      return !issue.isLoading && !(issue.invoice?.templateId === templateId && isCardIssuePaymentActive(issue.invoice))
    },
  },
)

export const topupCard = createAsyncThunk(
  'backend/topupCard',
  async ({ cardId, amount }: { cardId: string; amount: string }, { dispatch }) => {
    const sessionVersion = getPaymentSessionVersion()
    dispatch(patchBackendState({ isCardTopupLoading: true }))
    try {
      // Идемпотентность связывает подготовку и создание счёта на пополнение.
      const prepared = await apiPrepareCardTopup({ routeParams: { id: cardId }, data: { amount } })
      const idempotency = prepared?.status === 'ok' ? prepared.data?.idempotency : null

      if (sessionVersion !== getPaymentSessionVersion()) return null

      if (!idempotency) {
        reportFailure(dispatch, prepared)
        return null
      }

      const response = await apiTopupCard({
        routeParams: { id: cardId },
        data: { idempotency },
      })

      if (sessionVersion !== getPaymentSessionVersion()) return null

      if (!response?.data || response.status !== 'ok') {
        reportFailure(dispatch, response)
        return null
      }

      await Promise.all([dispatch(fetchCards()), dispatch(fetchTransactions())])

      if (sessionVersion !== getPaymentSessionVersion()) return null

      return response.data
    } finally {
      if (sessionVersion === getPaymentSessionVersion()) dispatch(patchBackendState({ isCardTopupLoading: false }))
    }
  },
  { condition: (_, { getState }) => !getState()[storeKey].isCardTopupLoading },
)

export const withdrawCard = createAsyncThunk(
  'backend/withdrawCard',
  async (
    { cardId, amount, bankId, phone }: { cardId: string; amount: string; bankId: WithdrawBankId; phone: string },
    { dispatch, getState },
  ) => {
    const sessionVersion = getPaymentSessionVersion()
    const backendBankId = getState()[storeKey].banks?.find((bank) => bank.id === bankId)?.bankId

    if (!backendBankId) {
      reportFailure(dispatch, null)
      return null
    }

    // Повторное подтверждение использует ключ подготовки, чтобы не дублировать вывод.
    const prepared = await apiPrepareCardWithdraw({
      routeParams: { id: cardId },
      data: { amount, bankId: backendBankId, phone },
    })
    const idempotency = prepared?.status === 'ok' ? prepared.data?.idempotency : null

    if (sessionVersion !== getPaymentSessionVersion()) return null

    if (!idempotency) {
      reportFailure(dispatch, prepared)
      return null
    }

    const response = await apiWithdrawCard({
      routeParams: { id: cardId },
      data: { idempotency },
    })

    if (sessionVersion !== getPaymentSessionVersion()) return null

    if (!response?.data || response.status !== 'ok') {
      reportFailure(dispatch, response)
      return null
    }

    await Promise.all([dispatch(fetchCards()), dispatch(fetchTransactions())])

    if (sessionVersion !== getPaymentSessionVersion()) return null

    return response
  },
)
